import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { axiosClient } from '../services/api';
import { useI18n } from '../i18n/i18nContext';

interface BookingDetails {
  serviceName: string;
  startTime: string;
  status: 'pending' | 'accepted' | 'declined';
}

export const InvitationResponsePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { t } = useI18n();
  const token = searchParams.get('token');
  const bookingId = searchParams.get('bookingId');

  const [details, setDetails] = useState<BookingDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [finalMessage, setFinalMessage] = useState('');

  useEffect(() => {
    if (!token || !bookingId) {
      setError('Invalid invitation link. Please check the URL and try again.');
      setIsLoading(false);
      return;
    }

    axiosClient.get(`/api/invitations/details?bookingId=${bookingId}&token=${token}`)
      .then(res => {
        setDetails(res.data);
        if (res.data.status !== 'pending') {
          setFinalMessage(`This invitation has already been ${res.data.status}.`);
        }
      })
      .catch(err => setError(err.response?.data?.message || 'Could not load invitation details.'))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, bookingId]);


  const handleBookingResponse = async (status: 'accepted' | 'declined') => {
    setIsLoading(true);
    setError('');
    try {
      await axiosClient.post('/api/invitations/respond', { bookingId, token, status });
      setFinalMessage(`Thank you! You have successfully ${status} the invitation.`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred while submitting your response.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const renderContent = () => {
    if (isLoading) return <p className="text-xl">{t.invitation.processing}</p>;
    if (error) return <p className="text-xl text-red-600 font-semibold">{error}</p>;
    if (finalMessage) return <p className="text-xl text-green-700 font-semibold">{finalMessage}</p>;

    if (details) {
      return (
        <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">You're Invited!</h2>
            <div className="text-left space-y-2">
                <p><strong>Service:</strong> {details.serviceName}</p>
                <p><strong>When:</strong> {new Date(details.startTime).toLocaleString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            <div className="flex justify-center space-x-4 mt-8">
                <button
                    onClick={() => handleBookingResponse('accepted')}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold px-6 py-3 rounded-lg transition-colors duration-300"
                >
                    {t.invitation.accept}
                </button>
                <button
                    onClick={() => handleBookingResponse('declined')}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3 rounded-lg transition-colors duration-300"
                >
                    {t.invitation.decline}
                </button>
            </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-12 md:py-20 text-center max-w-2xl">
        {renderContent()}
    </div>
  );
};